













































































import { Vue, Component, Prop, Watch, Emit } from "vue-property-decorator";
import { Getter } from "vuex-class";
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import FileStore from "@/types/settings/filestores/FileStore";
import { validationMixin } from "vuelidate";
import { required, minLength, maxLength, sameAs } from "vuelidate/lib/validators";
import settingsApi from "@/services/api.settings.service";
import FileStoreType from "@/types/settings/filestores/FileStoreType";

import WindowsConfiguration from "./WindowsConfiguration.vue";
import S3Configuration from "./S3Configuration.vue";

export interface EditorFileStore extends FileStore {
	isNew?: boolean;
}

@Component({
	mixins: [validationMixin],
	components: {
		"area-tree-select": AreaTreeSelect,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"windows-configuration": WindowsConfiguration,
		"s3-configuration": S3Configuration
	},
	model: {
		prop: "fileStore",
		event: "input"
	},
	validations: {
		currentFileStore: {
			groupId: {
				required
			}
		},
		isConfigurationValid: {
			checked: value => value == true
		}
	}
})
export default class FileStoreEditModal extends Vue {
	@Getter private getFeature: (featureNames: string[]) => boolean;

	@Prop({ type: Object, default: null }) public fileStore: EditorFileStore;

	private readonly windowsFilestoreTypeId = FileStoreType.Windows;
	private readonly s3FilestoreTypeId = FileStoreType.S3;

	private readonly fileStoreTypes: { value: number; text: string }[] = [
		{ value: FileStoreType.Windows, text: "Windows" },
		{ value: FileStoreType.S3, text: "S3" }
	];

	private readonly configurationComponents: { [fileStoreTypeId: number]: Vue | any } = {
		1: WindowsConfiguration,
		2: S3Configuration
	};

	private currentFileStore: EditorFileStore = null;

	private fileStoreModified: boolean = false;
	private isConfigurationValid: boolean = false;

	private unwatchFileStoreChanges: () => void;

	/**
	 * Gets the modal title to use, depending on whether we're editing or adding a file store.
	 */
	private get modalTitle() {
		return this.currentFileStore?.isNew ? "Add filestore" : "Edit filestore";
	}

	/**
	 * Gets a value indicating whether or not the modal is open.
	 */
	private get isEditModalOpen(): boolean {
		return !!this.currentFileStore;
	}

	/**
	 * Gets a value indicating whether the user has the ability to select a filestore type.
	 */
	private get canSelectFileStoreType(): boolean {
		return this.getFeature(["Settings", "FileStoreType"]);
	}

	@Watch("fileStore", { immediate: true })
	private onFileStoreChanged() {
		if (!this.fileStore) {
			return;
		}

		if (this.fileStore.isNew) {
			// If it's a new file store, just use the object as-is, the groupId can be left as null as we're
			// creating a new file store and the user must select one.
			this.currentFileStore = this.fileStore;
		} else {
			// Clone the file store to be edited. We swap group ID's with null for 0 here, as the Area Tree Select
			// considers null to be an empty value (when we want it to mean System Area).
			this.currentFileStore = {
				...this.fileStore,
				groupId: this.fileStore.groupId ?? 0
			};
		}

		this.fileStoreModified = false;
		this.unwatchFileStoreChanges = this.$watch("currentFileStore", this.whenUserChangesFileStore, { deep: true });

		this.$v.$touch();
	}

	/**
	 * Fired when the user makes a change to the data in the modal. Enables the save button, and removes the watch.
	 */
	private whenUserChangesFileStore() {
		this.fileStoreModified = true;

		this.unwatchFileStoreChanges();
		this.unwatchFileStoreChanges = null;
	}

	/**
	 * Handler called when the modal is closed.
	 */
	@Emit("input")
	private onClose() {
		this.currentFileStore = null;
		this.fileStoreModified = false;

		this.unwatchFileStoreChanges?.();
		this.unwatchFileStoreChanges = null;

		return null;
	}

	/**
	 * Handler called when the user saves their changes to the file store.
	 */
	private async onSave() {
		if (this.$v.$invalid) {
			return;
		}

		// Build the file store to send to the data service, swapping the 0 group ID for null.
		const fileStore = {
			...this.currentFileStore,
			groupId: this.currentFileStore.groupId == 0 ? null : this.currentFileStore.groupId
		};

		// Save the file store
		await settingsApi.upsertFileStore(fileStore);

		// Trigger a reload and close.
		this.$emit("reload-file-stores");
		this.onClose();
	}

	/**
	 * Handler called when the filestore configuration validation state changes.
	 */
	private onConfigurationValidationStateChanged(state: boolean) {
		this.isConfigurationValid = state;
		this.$v.$touch();
	}
}
